import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  type DisbursementBatchDetails,
  type DisbursementBatchIdentifier
} from '../../api-schema-types/payments/DisbursementBatchDomainDtos'
import { type UnlinkInstructionFromBatchPayload } from '../../api-schema-types/payments/DisbursementBatchPayloadDtos'
import {
  type DisbursementAccountDetails,
  type DisbursementInstructionDetails
} from '../../api-schema-types/payments/DisbursementInstructionDomainDtos'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'
import { type CreateDisbursementFeePayload } from '../../api-schema-types/payments/DisbursementFeePayloadDtos'
import { type CancelDisbursementInstructionPayload } from '../../api-schema-types/payments/DisbursementInstructionPayloadDtos'

export const paymentsApi = createApi({
  reducerPath: 'paymentsApi',

  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/payments`,
    prepareHeaders: (headers: Headers, { getState }) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  tagTypes: [
    'DisbursementBatches', 'DisbursementBatch',
    'DisbursementInstructions', 'DisbursementAccounts',
    'DisbursementFees'
  ],

  endpoints: (builder) => ({
    getOpenDisbursementBatches: builder.query({
      query: (showAllBatches: boolean) => ({
        url: `disbursements/batches/${showAllBatches ? 'all' : 'open'}`,
        params: { }
      }),
      providesTags: ['DisbursementBatches'],
      transformResponse: (response: DisbursementBatchDetails[], meta: any, arg: any) => paymentsMapper().mapDisbursementBatches(response, meta, arg)
    }),

    getDisbursementBatch: builder.query({
      query: ({ disbursementBatchId }) => ({
        url: `disbursements/batches/${disbursementBatchId}`,
        params: { }
      }),
      providesTags: ['DisbursementBatch'],
      transformResponse: (response: DisbursementBatchDetails, meta: any, arg: any) => paymentsMapper().mapDisbursementBatch(response, meta, arg)
    }),

    createDisbursementBatch: builder.mutation({
      query: ({ currencyCode, schedule }) => ({
        url: 'disbursements/batches/create',
        method: 'POST',
        body: {
          currencyCode,
          schedule
        }
      }),
      transformResponse: (response: DisbursementBatchIdentifier, meta: any, arg: any) => paymentsMapper().mapDisbursementBatchIdentifier(response, meta, arg),
      invalidatesTags: ['DisbursementBatches', 'DisbursementInstructions']
    }),

    cancelDisbursementBatch: builder.mutation({
      query: (disbursementBatchId: number) => ({
        url: `disbursements/batches/${disbursementBatchId}/cancel`,
        method: 'POST',
        body: { }
      }),
      invalidatesTags: ['DisbursementBatches', 'DisbursementBatch', 'DisbursementInstructions']
    }),

    exportDisbursementBatch: builder.mutation({
      query: (disbursementBatchId: number) => ({
        url: `disbursements/batches/${disbursementBatchId}/export`,
        method: 'POST',
        body: { }
      }),
      invalidatesTags: ['DisbursementBatches', 'DisbursementBatch', 'DisbursementInstructions']
    }),

    downloadBatchCsv: builder.query({
      query: (disbursementBatchId: number) => ({
        url: `disbursements/batches/${disbursementBatchId}/download`,
        responseHandler: async (response) => await response.text()
      })
    }),

    processDisbursementBatch: builder.mutation({
      query: (disbursementBatchId: number) => ({
        url: `disbursements/batches/${disbursementBatchId}/process`,
        method: 'POST',
        body: { }
      }),
      invalidatesTags: ['DisbursementBatches', 'DisbursementBatch', 'DisbursementInstructions']
    }),

    unlinkDisbursementInstructions: builder.mutation({
      query: (arg: UnlinkInstructionFromBatchPayload) => ({
        url: `disbursements/batches/${arg.disbursementBatchId}/unlink-instructions`,
        method: 'POST',
        body: {
          disbursementInstructionIds: arg.disbursementInstructionIds
        }
      }),
      invalidatesTags: ['DisbursementBatches', 'DisbursementBatch', 'DisbursementInstructions']
    }),

    unlinkFailedDisbursementInstructions: builder.mutation({
      query: (arg: UnlinkInstructionFromBatchPayload) => ({
        url: `disbursements/batches/${arg.disbursementBatchId}/unlink-failed-instructions`,
        method: 'POST',
        body: {
          disbursementInstructionIds: arg.disbursementInstructionIds
        }
      }),
      invalidatesTags: ['DisbursementBatches', 'DisbursementBatch', 'DisbursementInstructions']
    }),

    // ===============================================================================================================

    getPendingDisbursementInstructions: builder.query({
      query: () => ({
        url: 'disbursements/maintenance/pending',
        method: 'GET',
        params: { }
      }),
      providesTags: ['DisbursementInstructions'],
      transformResponse: (response: DisbursementInstructionDetails[], meta: any, arg: any) => paymentsMapper().mapDisbursementInstructions(response, meta, arg)
    }),

    // ===============================================================================================================

    // We use the search endpoint below rather
    // getPendingAssetHolderDisbursementInstructions: builder.query({
    //   query: (assetHolderId: number) => ({
    //     url: `disbursements/asset-holders/${assetHolderId}/instructions`,
    //     method: 'GET'
    //   }),
    //   providesTags: ['DisbursementInstructions'],
    //   transformResponse: (response: DisbursementInstructionSummary[], meta: any, arg: any) => response
    // }),

    getAssetHolderDisbursementInstructions: builder.query({
      query: (assetHolderId: number) => ({
        url: 'disbursements/maintenance/search',
        method: 'POST',
        body: {
          assetHolderId,
          page: 0,
          pageSize: 500
        }
      }),
      providesTags: ['DisbursementInstructions'],
      transformResponse: (response: DisbursementInstructionDetails[], meta: any, arg: any) => response

    }),

    getAssetHolderDisbursementAccounts: builder.query({
      query: (assetHolderId: number) => ({
        url: `disbursements/asset-holders/${assetHolderId}/accounts/all`,
        method: 'GET'
      }),
      providesTags: ['DisbursementAccounts'],
      transformResponse: (response: DisbursementAccountDetails[], meta: any, arg: any) => paymentsMapper().mapDisbursementAccounts(response, meta, arg)
    }),

    cancelDisbursementInstruction: builder.mutation({
      query: (payload: { assetHolderId: number, body: CancelDisbursementInstructionPayload }) => ({
        url: `disbursements/asset-holders/${payload.assetHolderId}/instructions/cancel`,
        method: 'POST',
        body: payload.body
      }),
      invalidatesTags: ['DisbursementInstructions']
    }),

    // ===============================================================================================================

    getDisbursementFees: builder.query({
      query: () => ({
        url: 'disbursements/fees',
        method: 'GET'
      }),
      providesTags: ['DisbursementFees']
    }),

    createDisbursementFee: builder.mutation<undefined, Partial<CreateDisbursementFeePayload>>({
      query: (body) => ({
        url: 'disbursements/fees/create',
        method: 'POST',
        body
      }),
      invalidatesTags: ['DisbursementFees']
    })

  })
})

export const useDownloadBatchCsvLazyQuery = paymentsApi.endpoints.downloadBatchCsv.useLazyQuery

export const useGetOpenDisbursementBatchesQuery = paymentsApi.endpoints.getOpenDisbursementBatches.useQuery
export const useGetDisbursementBatchQuery = paymentsApi.endpoints.getDisbursementBatch.useQuery
export const useCreateDisbursementBatchMutation = paymentsApi.endpoints.createDisbursementBatch.useMutation
export const useCancelDisbursementBatchMutation = paymentsApi.endpoints.cancelDisbursementBatch.useMutation
export const useExportDisbursementBatchMutation = paymentsApi.endpoints.exportDisbursementBatch.useMutation
export const useProcessDisbursementBatchMutation = paymentsApi.endpoints.processDisbursementBatch.useMutation
export const useUnlinkDisbursementInstructionsMutation = paymentsApi.endpoints.unlinkDisbursementInstructions.useMutation
export const useUnlinkFailedDisbursementInstructionsMutation = paymentsApi.endpoints.unlinkFailedDisbursementInstructions.useMutation

export const useGetPendingDisbursementInstructionsQuery = paymentsApi.endpoints.getPendingDisbursementInstructions.useQuery
export const useCancelDisbursementInstructionMutation = paymentsApi.endpoints.cancelDisbursementInstruction.useMutation

export const useGetAssetHolderDisbursementAccountsQuery = paymentsApi.endpoints.getAssetHolderDisbursementAccounts.useQuery
export const useGetAssetHolderDisbursementInstructionsQuery = paymentsApi.endpoints.getAssetHolderDisbursementInstructions.useQuery
export const useGetDisbursementFeesQuery = paymentsApi.endpoints.getDisbursementFees.useQuery
export const useCreateDisbursementFeeMutation = paymentsApi.endpoints.createDisbursementFee.useMutation

// ====================================================================================================================

 
export const paymentsMapper = () => {
  const mapDisbursementBatches = (responseData: DisbursementBatchDetails[], responseMeta: any, responseArgs: any): DisbursementBatchDetails[] => {
    console.log(`[API] paymentsMapper Loaded ${responseData.length} disbursement batches`)
    return responseData
  }

  const mapDisbursementBatch = (responseData: DisbursementBatchDetails, responseMeta: any, responseArgs: any): DisbursementBatchDetails => {
    console.log(`[API] paymentsMapper Loaded batch ${responseData.disbursementBatchId} `)
    return responseData
  }

  const mapDisbursementBatchIdentifier = (responseData: DisbursementBatchIdentifier, responseMeta: any, responseArgs: any): DisbursementBatchIdentifier => {
    console.log(`[API] paymentsMapper Updated batch ${responseData.disbursementBatchId} `)
    return responseData
  }

  const mapDisbursementInstructions = (responseData: DisbursementInstructionDetails[], responseMeta: any, responseArgs: any): DisbursementInstructionDetails[] => {
    console.log(`[API] paymentsMapper Loaded ${responseData.length} disbursement instructions`)
    return responseData
  }

  const mapDisbursementAccounts = (responseData: DisbursementAccountDetails[], responseMeta: any, responseArgs: any): DisbursementAccountDetails[] => {
    console.log(`[API] paymentsMapper Loaded ${responseData.length} disbursement accounts`)
    // return responseData.filter((da) => da.active)
    return responseData
  }

  return {
    mapDisbursementBatches,
    mapDisbursementBatch,
    mapDisbursementInstructions,
    mapDisbursementBatchIdentifier,
    mapDisbursementAccounts
  }
}
